// modules/Menu/MenuOperation.js
// 菜单操作处理模块

import EquipmentSystem from '../EquipmentSystem.js';
import { getWorldScene } from '../SceneRegistry.js';
import GameStateStore from '../GameStateStore.js';

export default class MenuOperation {
    constructor(controller) {
        this.controller = controller;
    }
    
    /**
     * 初始化菜单数据
     * @param {Object} playerData - 玩家数据
     */
    initMenuData(playerData) {
        const gsPlayer = GameStateStore.getPlayer();
        if (gsPlayer) {
            const player = playerData || gsPlayer;

            // 道具列表（根据分类筛选）
            this.controller.state.menuOptions.items = this.filterItemsByCategory(player.inventory || []);

            // 装备列表
            this.controller.state.menuOptions.equipment = this.formatEquipmentList(player.equipment || [], player);

            // 状态信息
            this.controller.state.menuOptions.status = [
                { label: '等级', value: player.level || 1 },
                { label: '生命值', value: `${player.hp}/${player.maxHp}` },
                { label: '攻击力', value: player.attack },
                { label: '防御力', value: player.defense },
                { label: '金钱', value: player.money || 0 },
                { label: '经验值', value: player.exp || 0 },
                { label: '职业', value: player.class || '猎人' }
            ];
            
            // 初始化组队菜单选项
            this.updateTeamMenuOptions();
        }
    }
    
    /**
     * 格式化装备列表
     * @param {Array} equipmentArray - 装备数组
     * @param {Object} playerData - 玩家数据
     * @returns {Array} 格式化后的装备列表
     */
    formatEquipmentList(equipmentArray, playerData) {
        const formattedList = [];
        const equippedMap = {};
        const inventory = playerData.inventory || [];

        // 标记已装备的物品
        if (equipmentArray) {
            equipmentArray.forEach(item => {
                if (item && item.slot) {
                    const normalizedSlot = EquipmentSystem.normalizeSlot(item.slot);
                    equippedMap[normalizedSlot] = item;
                }
            });
        }

        // 添加已装备的物品（带标识）
        EquipmentSystem.SLOT_ORDER.forEach(slot => {
            const equippedItem = equippedMap[slot];
            if (equippedItem) {
                let displayText = `✓ ${equippedItem.name}`;

                if (equippedItem.damage) {
                    displayText += ` (伤害+${equippedItem.damage})`;
                } else if (equippedItem.defense) {
                    displayText += ` (防御+${equippedItem.defense})`;
                }

                formattedList.push({
                    name: displayText,
                    type: equippedItem.type,
                    slot: equippedItem.slot,
                    originalItem: equippedItem,
                    isEquipped: true
                });
            }
        });

        // 添加未装备的物品（从背包中筛选）
        inventory.forEach(item => {
            if (item && (item.type === 'equipment' || item.type === 'weapon' || item.type === 'armor' || item.type === 'helmet')) {
                let itemSlot = item.slot || EquipmentSystem.getSlotFromType(item.type);
                if (itemSlot) {
                    itemSlot = EquipmentSystem.normalizeSlot(itemSlot);
                }
                if (itemSlot && !equippedMap[itemSlot]) {
                    let displayText = `  ${item.name}`;

                    if (item.damage) {
                        displayText += ` (伤害+${item.damage})`;
                    } else if (item.defense) {
                        displayText += ` (防御+${item.defense})`;
                    }

                    formattedList.push({
                        name: displayText,
                        type: item.type,
                        slot: itemSlot,
                        originalItem: item,
                        isEquipped: false
                    });
                }
            }
        });

        // 如果没有装备，显示提示
        if (formattedList.length === 0) {
            formattedList.push({
                name: '无装备',
                type: 'empty',
                slot: 'none',
                isEquipped: false
            });
        }

        return formattedList;
    }
    
    /**
     * 获取菜单对应的世界场景（来自 previousSceneKey，而非写死 WorldScene）
     * @returns {Phaser.Scene|null}
     * @private
     */
    _getWorldSceneForMenu() {
        const previousSceneKey = this.controller.scene.previousSceneKey || null;
        return getWorldScene(this.controller.scene.scene, previousSceneKey);
    }
    
    /**
     * 更新组队菜单选项
     */
    updateTeamMenuOptions() {
        const worldScene = this._getWorldSceneForMenu();
        if (!worldScene || !worldScene.teamManager) {
            this.controller.state.menuOptions.team = ['创建队伍'];
            return;
        }
        
        const teamManager = worldScene.teamManager;
        const teamId = teamManager.getTeamId();
        const isLeader = teamManager.isTeamLeader();
        
        if (teamId) {
            // 在队伍中
            if (isLeader) {
                this.controller.state.menuOptions.team = ['解散队伍', '离开队伍'];
            } else {
                this.controller.state.menuOptions.team = ['离开队伍'];
            }
        } else {
            // 不在队伍中
            this.controller.state.menuOptions.team = ['创建队伍'];
        }
    }
    
    /**
     * 更新选中项
     * @param {Object} playerData - 玩家数据
     */
    updateSelectedOption(playerData) {
        // 装备菜单使用特殊更新逻辑
        if (this.controller.state.currentMenu === 'equipment') {
            this.updateEquipmentMenuSelection();
            return;
        }

        // 更新选中项的视觉效果
        this.controller.menuUI.updateSelection(this.controller.state.selectedOption);

        // 更新信息面板
        this.updateInfoPanel(playerData);
    }
    
    /**
     * 更新装备菜单选择
     */
    updateEquipmentMenuSelection(playerData) {
        const data = playerData || GameStateStore.getPlayer();
        this.controller.equipmentMenuUI.updateSelection(
            data,
            this.controller.state.selectedOption,
            this.controller.state.equipmentInventoryIndex
        );
    }

    /**
     * 装备菜单空格操作
     */
    executeEquipmentAction(playerData) {
        this.controller.equipmentHandler.handleSlotAction(
            this.controller.state.selectedOption,
            this.controller.state.equipmentInventoryIndex,
            playerData,
            () => {
                this.initMenuData(playerData);
                this.controller.navigation.showEquipmentMenu(playerData);
            }
        );
    }
    
    /**
     * 更新信息面板
     * @param {Object} playerData - 玩家数据
     */
    updateInfoPanel(playerData) {
        const options = this.controller.state.menuOptions[this.controller.state.currentMenu];
        const option = options ? options[this.controller.state.selectedOption] : null;
        this.controller.menuUI.updateInfoPanel(this.controller.state.currentMenu, option, playerData);
    }
    
    /**
     * 执行选中的选项
     * @param {Object} playerData - 玩家数据
     * @param {Array} displayOptions - 显示的选项列表（可能包含返回选项）
     */
    executeSelectedOption(playerData, displayOptions = null) {
        // 装备菜单：selectedOption 为槽位索引，空格执行装备/卸下，不走列表「返回」逻辑
        if (this.controller.state.currentMenu === 'equipment') {
            this.executeEquipmentAction(playerData);
            return;
        }

        let options = this.controller.state.menuOptions[this.controller.state.currentMenu];
        const originalOptionsLength = options ? options.length : 0;
        
        // 对于非主菜单，检查是否选择了返回选项（在列表末尾）
        if (this.controller.state.currentMenu !== 'main' && this.controller.state.currentMenu !== 'team') {
            if (this.controller.state.selectedOption >= originalOptionsLength) {
                this.controller.navigation.showMenu('main', playerData);
                return;
            }
        }
        
        // 如果提供了显示选项（包含返回选项），使用它来获取实际选项
        if (displayOptions && this.controller.state.selectedOption < displayOptions.length) {
            const displayOption = displayOptions[this.controller.state.selectedOption];
            // 检查是否是返回选项
            if (displayOption && (displayOption.isReturn === true || displayOption.name === '返回' || displayOption.label === '返回')) {
                // 返回主菜单
                this.controller.navigation.showMenu('main', playerData);
                return;
            }
        }

        if (!options || options.length === 0) {
            return;
        }

        const option = options[this.controller.state.selectedOption];

        switch(this.controller.state.currentMenu) {
            case 'main':
                this.controller.navigation.handleMainMenuOption();
                break;
            case 'items':
                if (option) {
                    this.controller.itemHandler.useItem(option, playerData, () => {
                        this.initMenuData(playerData);
                        this.controller.navigation.showMenu('items', playerData);
                    });
                }
                break;
            case 'status':
                // 状态菜单：只查看，无操作（返回选项已在上面处理）
                break;
            case 'team':
                this.handleTeamMenuOption();
                break;
        }
    }
    
    /**
     * 处理组队菜单选项
     */
    handleTeamMenuOption() {
        const teamOption = this.controller.state.menuOptions.team[this.controller.state.selectedOption];
        if (!teamOption) return;
        
        // 获取世界场景的 teamManager
        const worldScene = this._getWorldSceneForMenu();
        if (!worldScene || !worldScene.teamManager) {
            this.controller.menuUI.showMessage('多人游戏未连接');
            return;
        }
        
        const teamManager = worldScene.teamManager;
        const networkManager = worldScene.networkManager;
        
        switch(teamOption) {
            case '创建队伍':
                if (teamManager.getTeamId()) {
                    this.controller.menuUI.showMessage('你已经在队伍中');
                } else {
                    // 标记在菜单中创建队伍，TeamManager 会在成功后关闭菜单
                    teamManager.setMenuContext(this.controller.scene);
                    teamManager.createTeam();
                }
                break;
            case '离开队伍':
                if (!teamManager.getTeamId()) {
                    this.controller.menuUI.showMessage('你不在任何队伍中');
                } else {
                    teamManager.leaveTeam();
                    this.controller.menuUI.showMessage('正在离开队伍...');
                    // 延迟更新菜单
                    this.controller.scene.time.delayedCall(500, () => {
                        this.updateTeamMenu();
                    });
                }
                break;
            case '解散队伍':
                if (!teamManager.getTeamId()) {
                    this.controller.menuUI.showMessage('你不在任何队伍中');
                } else if (!teamManager.isTeamLeader()) {
                    this.controller.menuUI.showMessage('只有队长可以解散队伍');
                } else {
                    if (networkManager && networkManager.isConnected()) {
                        networkManager.disbandTeam();
                        this.controller.menuUI.showMessage('正在解散队伍...');
                        // 延迟更新菜单
                        this.controller.scene.time.delayedCall(500, () => {
                            this.updateTeamMenu();
                        });
                    }
                }
                break;
        }
    }
    
    /**
     * 更新组队菜单
     */
    updateTeamMenu() {
        const worldScene = this._getWorldSceneForMenu();
        if (!worldScene || !worldScene.teamManager) {
            return;
        }
        
        const teamManager = worldScene.teamManager;
        const teamId = teamManager.getTeamId();
        const isLeader = teamManager.isTeamLeader();
        const teamMembers = teamManager.getTeamMembers();
        
        // 更新组队菜单选项
        if (teamId) {
            // 在队伍中
            if (isLeader) {
                this.controller.state.menuOptions.team = ['解散队伍', '离开队伍'];
            } else {
                this.controller.state.menuOptions.team = ['离开队伍'];
            }
        } else {
            // 不在队伍中
            this.controller.state.menuOptions.team = ['创建队伍'];
        }
        
        // 显示组队菜单
        this.controller.state.selectedOption = 0;
        this.controller.navigation.showMenu('team', GameStateStore.getPlayer());
    }
    
    /**
     * 根据分类筛选道具
     * @param {Array} inventory - 背包数组
     * @returns {Array} 筛选并合并后的道具列表
     */
    filterItemsByCategory(inventory) {
        const category = this.controller.state.itemCategory;
        
        // 先筛选
        let filteredItems = inventory;
        if (category !== 'all') {
            filteredItems = inventory.filter(item => {
                if (!item || !item.type) return false;
                
                switch(category) {
                    case 'consumable':
                        return item.type === 'consumable';
                    case 'equipment':
                        return item.type === 'equipment' || item.type === 'weapon' || item.type === 'armor' || item.type === 'helmet';
                    case 'vehicle_equipment':
                        return item.type === 'vehicle_equipment';
                    case 'material':
                        return item.type === 'material';
                    default:
                        return true;
                }
            });
        }
        
        // 合并相同物品
        return this.mergeSameItems(filteredItems);
    }
    
    /**
     * 合并相同物品
     * @param {Array} items - 物品数组
     * @returns {Array} 合并后的物品列表，每个物品包含 count 和 items 属性
     */
    mergeSameItems(items) {
        const itemMap = new Map();
        
        items.forEach(item => {
            if (!item) return;
            
            // 使用 id 或 name 作为唯一标识
            const key = item.id || item.name;
            if (!key) return;
            
            if (!itemMap.has(key)) {
                // 创建合并后的物品对象
                itemMap.set(key, {
                    ...item, // 保留原始属性
                    count: 1,
                    items: [item] // 保存所有相同物品的引用
                });
            } else {
                // 增加数量并添加引用
                const merged = itemMap.get(key);
                merged.count++;
                merged.items.push(item);
            }
        });
        
        return Array.from(itemMap.values());
    }
    
    /**
     * 切换道具分类
     * @param {string} category - 分类key
     */
    switchItemCategory(category) {
        this.controller.state.itemCategory = category;
        this.controller.state.selectedOption = 0; // 重置选中项
        this.initMenuData(GameStateStore.getPlayer());
        this.controller.navigation.showMenu('items', GameStateStore.getPlayer());
    }
}

